"use no memo";

import type { ColumnDef } from "@tanstack/react-table";
import { Status } from "@repo/database";
import { capitalize, highlightText } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { ProductCategory } from "~/providers/product-provider";
import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { Switch } from "~/components/ui/switch";
import { useProduct } from "~/hooks/use-product";
import { useProductCategoryManager } from "~/managers/use-product-category-manager";

export const Route = createFileRoute(
  "/_main/settings/_layout/product-categories/",
)({
  component: ProductCategories,
});

function ProductCategories() {
  const { t } = useTranslation("common");

  const { productCategories } = useProduct();

  const { openProductCategoryDialog, updateProductCategoryStatus } =
    useProductCategoryManager();

  const columns: ColumnDef<ProductCategory>[] = [
    {
      id: t("name"),
      accessorKey: "name",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      cell: ({ row: { original: productcategory }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html: highlightText(productcategory.name, globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: t("ProductCategory.position"),
      accessorKey: "position",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("ProductCategory.position")}
        />
      ),
      cell: ({ row: { original: productcategory }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(
                  productcategory.position.toString(),
                  globalFilter,
                ) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: t("status"),
      accessorKey: "status",
      size: 1,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("status")} />
      ),
      cell: ({ row: { original: productCategory } }) => {
        return (
          <Switch
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(checked) =>
              updateProductCategoryStatus(
                productCategory.id,
                checked ? Status.active : Status.inactive,
              )
            }
            className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-red-500"
            checked={productCategory.status === Status.active ? true : false}
          />
        );
      },
    },
  ];

  return (
    <>
      <SettingsHeader
        title={t("product_categories")}
        buttonLabel={capitalize(t("ProductCategory.new_product_category"))}
        buttonAction={async () => {
          await openProductCategoryDialog();
        }}
      />

      <DataTable
        defaultSorting={[{ id: t("ProductCategory.position"), desc: false }]}
        columns={columns}
        data={productCategories}
        onSelect={openProductCategoryDialog}
      />
    </>
  );
}
